import {
  Badge,
  CopyIcon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EllipsisVerticalIcon,
  Table,
  TableCell,
  TableRow,
} from '@fukuri-admins/ui/common';
import { iconButtonVariants } from '@fukuri-admins/ui/common/IconButton/logic';
import styles from './styles.module.scss';

/**
 * 利用者テーブルの行データ型
 *
 * 表示整形済みの値を受け取る（converter で API 型から変換される）
 */
type UserRow = {
  /** 利用者の一意な識別子（API の user.id） */
  id: number;
  /** 所属導入企業名 */
  clientName: string;
  /** 利用者名 */
  name: string;
  /** アクティベーション済みか */
  isActivated: boolean;
  /**
   * アクティベーションコード
   *
   * isActivated が false の場合は具体的なコード文字列、true の場合は undefined
   */
  activationCode?: string;
  /** メールアドレス */
  email: string;
  /**
   * 電話番号
   *
   * isActivationCodeEnabled が true のときのみ表示
   */
  phoneNumber?: string;
  /**
   * 利用開始日（表示整形済み）
   *
   * 未設定時は '-'
   */
  billableFrom: string;
  /** 作成日時（表示整形済み） */
  createdAt: string;
  /** 更新日時（表示整形済み） */
  updatedAt: string;
};

type Props = {
  /** テーブルに表示する利用者行データ */
  rows: UserRow[];
  /**
   * アクティベーションコード列（ログイン・アクチコード・電話番号）を表示するか
   *
   * `getEnvFeatureFlag().enableDevelopment || getEnvFeatureFlag().enableInternal` の値を渡す
   */
  isActivationCodeEnabled: boolean;
  /**
   * 行の「編集」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onEditUser: (userId: number) => void;
  /**
   * 行の「削除」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onDeleteUser: (userId: number) => void;
  /**
   * 行クリック時のコールバック（利用者詳細画面への遷移）
   *
   * @param userId 操作対象の利用者 ID
   */
  onRowClick: (userId: number) => void;
  /**
   * アクティベーションコードのコピーボタン押下時のコールバック
   *
   * @param code コピーするアクティベーションコード
   */
  onCopyActivationCode: (code: string) => void;
};

/**
 * 利用者一覧テーブル（テーブル本体のみ）
 *
 * - isActivationCodeEnabled が true のとき: ログイン・アクティベーションコード・電話番号列を表示
 * - 利用開始日・更新日時列は常時表示する
 * - アクティベーションコードが存在する行には Copy ボタンを表示し、クリップボードへコピーできる
 * - 設定アクション: 行内の DropdownMenu 経由で編集・削除コールバックを呼び出す
 * - ページャーは親の UserListPresentation が担う
 */
export const UsersTable: React.FC<Props> = ({
  rows,
  isActivationCodeEnabled,
  onEditUser,
  onDeleteUser,
  onRowClick,
  onCopyActivationCode,
}) => (
  <Table
    scrollable
    headRowClassName={styles.headRow}
    headers={[
      { label: '利用企業名', className: styles.clientNameCol },
      { label: '名前', className: styles.nameCol },
      ...(isActivationCodeEnabled
        ? [
            { label: 'ログイン', className: styles.loginCol },
            { label: 'アクティベーションコード', className: styles.activationCodeCol },
          ]
        : []),
      { label: 'メールアドレス', className: styles.emailCol },
      ...(isActivationCodeEnabled ? [{ label: '電話番号', className: styles.phoneNumberCol }] : []),
      { label: '利用開始日', className: styles.billableFromCol },
      { label: '作成日時', className: styles.createdAtCol },
      { label: '更新日時', className: styles.updatedAtCol },
      { label: '設定', className: styles.actionCol },
    ]}
  >
    {rows.map(row => (
      <TableRow key={row.id} onClick={() => onRowClick(row.id)}>
        <TableCell>{row.clientName}</TableCell>
        <TableCell>{row.name}</TableCell>
        {isActivationCodeEnabled && (
          <>
            <TableCell>
              <Badge
                size='sm'
                variant='subtle'
                status={row.isActivated ? 'success' : 'neutral'}
                label={row.isActivated ? '完了' : '未完了'}
              />
            </TableCell>
            <TableCell>
              {row.activationCode ? (
                <div className={styles.activationCode}>
                  <span>{row.activationCode}</span>
                  <button
                    type='button'
                    aria-label='アクティベーションコードをコピー'
                    className={iconButtonVariants({ variant: 'ghost', size: 'sm' })}
                    onClick={e => {
                      e.stopPropagation();
                      onCopyActivationCode(row.activationCode ?? '');
                    }}
                  >
                    <CopyIcon size='xs' />
                  </button>
                </div>
              ) : (
                '-'
              )}
            </TableCell>
          </>
        )}
        <TableCell>{row.email ?? '-'}</TableCell>
        {isActivationCodeEnabled && <TableCell>{row.phoneNumber ?? '-'}</TableCell>}
        <TableCell>{row.billableFrom}</TableCell>
        <TableCell>{row.createdAt}</TableCell>
        <TableCell>{row.updatedAt}</TableCell>
        <TableCell align='center'>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <button
                type='button'
                className={iconButtonVariants({ variant: 'ghost', size: 'sm' })}
                aria-label='設定メニューを開く'
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVerticalIcon />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' onClick={e => e.stopPropagation()}>
              <DropdownMenuItem onSelect={() => onEditUser(row.id)}>編集</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className={styles.deleteItem} onSelect={() => onDeleteUser(row.id)}>
                削除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>
    ))}
  </Table>
);
